import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidateTimeOffQueries } from '@/modules/common/utils/query-invalidation';

import { createTimeOffRequest } from '../api/time-offs.api';
import {
  extractTimeOffDayValidationErrors,
  TimeOffDayValidationError,
} from '../helpers/day-validation-errors.helper';
import { CreateTimeOffRequestRequest } from '../types/dto/create-time-off-request.dto';

interface UseCreateTimeOffRequestParams {
  onSuccess?: () => void;
  /**
   * Fired instead of the error toast when the backend rejects the request with
   * day-level validation errors (code 2808) — the caller shows the overlap modal.
   */
  onDayValidationError?: (errors: TimeOffDayValidationError[]) => void;
}

/**
 * Creates a time-off request. On success invalidates the time-off queries (so
 * the list reflects the new request) and runs `onSuccess` (navigate to the list).
 * Day-level validation failures (code 2808) are routed to `onDayValidationError`
 * (overlap modal) instead of a generic toast.
 */
export const useCreateTimeOffRequest = ({
  onSuccess,
  onDayValidationError,
}: UseCreateTimeOffRequestParams = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.timeOff');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: (payload: CreateTimeOffRequestRequest) => createTimeOffRequest(payload),
    onSuccess: async () => {
      await invalidateTimeOffQueries(queryClient);
      displayToastSuccess(t('toasts.createSuccess'));
      onSuccess?.();
    },
    onError: error => {
      const dayErrors = extractTimeOffDayValidationErrors(error);
      if (dayErrors && dayErrors.length > 0) {
        onDayValidationError?.(dayErrors);
        return;
      }
      displayToastErrorFromApiWithFallback(error, 'toasts.createError');
    },
  });
};
